import { useState } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { TrendingUp, ArrowUpRight, Target, BarChart3, CheckCircle2 } from 'lucide-react';
import { useFadeInObserver } from '@/hooks/use-fade-in-observer';

interface CaseStudy {
  title: string;
  niche: string;
  summary: string;
  tags: string[];
  metrics: {
    label: string;
    before: string;
    after: string;
    growth: string;
  }[];
  challenge: string;
  solution: string[];
  results: string[];
}

const caseStudies: CaseStudy[] = [
  {
    title: 'Bangladesh E-commerce SEO',
    niche: 'Fashion Brand · Dhaka',
    summary: 'Grew organic traffic by 220% in 6 months for a local fashion brand competing against global retailers.',
    tags: ['SEO', 'Link Building', 'Content'],
    metrics: [
      { label: 'Organic Traffic', before: '8K/mo', after: '25.6K/mo', growth: '+220%' },
      { label: 'Keywords Ranked', before: '34', after: '154', growth: '+120' },
      { label: 'Domain Authority', before: '15', after: '38', growth: '+23' },
    ],
    challenge: 'A local fashion brand struggled to compete with international e-commerce giants in organic search, with most product pages buried beyond page 5 of Google.',
    solution: [
      'Complete technical SEO audit and Core Web Vitals fixes',
      'Restructured product category architecture for crawl efficiency',
      'Built 80+ high-authority backlinks from fashion blogs',
      'Bengali + English bilingual content strategy targeting long-tail buyer keywords',
    ],
    results: [
      '220% growth in organic monthly sessions',
      '120+ new keywords ranking on Google Page 1',
      'Domain Authority increased from 15 to 38',
      '3.5x increase in organic revenue contribution',
    ],
  },
  {
    title: 'Real Estate SEO — USA Client',
    niche: 'Real Estate Agency · Florida',
    summary: 'Ranked 45+ commercial keywords on Page 1 of Google within 4 months for a competitive Florida market.',
    tags: ['Local SEO', 'Technical SEO', 'GMB'],
    metrics: [
      { label: 'Page 1 Keywords', before: '6', after: '51', growth: '+45' },
      { label: 'Lead Form Submits', before: '12/mo', after: '78/mo', growth: '+550%' },
      { label: 'Map Pack Visibility', before: '8%', after: '64%', growth: '+700%' },
    ],
    challenge: 'A boutique real estate agency in Florida needed to break into a market dominated by Zillow, Realtor.com, and large franchises.',
    solution: [
      'Hyper-local landing pages for 25 neighborhoods',
      'Google Business Profile optimization with weekly posts',
      'Structured data implementation for property listings',
      'Citation building across 50+ local directories',
    ],
    results: [
      '45+ keywords on Page 1 within 4 months',
      '550% increase in qualified lead form submissions',
      'Featured in Map Pack for 64% of target queries',
      'Reduced cost-per-lead by 73% vs paid channels',
    ],
  },
  {
    title: 'Restaurant Local SEO',
    niche: 'Multi-location Restaurant · Dhaka',
    summary: 'Achieved #1 Google Maps ranking and 3x more phone calls for a Dhaka-based restaurant chain.',
    tags: ['Local SEO', 'GMB', 'Reviews'],
    metrics: [
      { label: 'Map Pack Ranking', before: '#7', after: '#1', growth: 'Top spot' },
      { label: 'Phone Calls', before: '45/wk', after: '142/wk', growth: '+215%' },
      { label: 'Direction Requests', before: '120/mo', after: '480/mo', growth: '+300%' },
    ],
    challenge: 'A 3-location restaurant chain was invisible on Google Maps and losing customers to competitors with better local presence.',
    solution: [
      'Optimized all 3 GMB profiles with photos, posts, and Q&A',
      'Implemented review acquisition system (180+ new reviews)',
      'Built location-specific landing pages with schema',
      'Local citation cleanup and NAP consistency audit',
    ],
    results: [
      '#1 Google Maps ranking for primary keywords',
      '3x increase in phone call conversions',
      '300% more direction requests from Google',
      '40% increase in walk-in customers reported',
    ],
  },
];

const CaseStudyCard = ({ cs, delay, onOpen }: { cs: CaseStudy; delay: number; onOpen: () => void }) => {
  const { ref, isVisible } = useFadeInObserver(delay);
  return (
    <div
      ref={ref}
      className={`transition-all duration-700 ${
        isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
      }`}
    >
      <Card className="group h-full border-border hover:border-primary/50 hover:shadow-[var(--shadow-elegant)] transition-all duration-300 hover:-translate-y-2">
        <CardContent className="p-6 flex flex-col h-full">
          <div className="flex items-start justify-between mb-4">
            <div>
              <h3 className="text-xl font-bold mb-1 group-hover:text-primary transition-colors">{cs.title}</h3>
              <p className="text-sm text-muted-foreground">{cs.niche}</p>
            </div>
            <div className="p-2 rounded-lg bg-primary/10 group-hover:bg-primary group-hover:rotate-12 transition-all duration-300">
              <TrendingUp className="w-5 h-5 text-primary group-hover:text-primary-foreground transition-colors" aria-hidden="true" />
            </div>
          </div>
          <p className="text-foreground/80 text-sm mb-5 leading-relaxed">{cs.summary}</p>
          <div className="space-y-2 mb-5 p-4 rounded-lg bg-muted/50">
            {cs.metrics.map((m) => (
              <div key={m.label} className="flex items-center justify-between text-sm">
                <span className="text-muted-foreground">{m.label}</span>
                <span className="font-bold text-primary">{m.growth}</span>
              </div>
            ))}
          </div>
          <div className="flex flex-wrap gap-2 mb-5">
            {cs.tags.map((tag) => (
              <Badge key={tag} variant="secondary" className="text-xs">{tag}</Badge>
            ))}
          </div>
          <Button variant="outline" className="mt-auto group/btn" onClick={onOpen}>
            View Case Study
            <ArrowUpRight className="ml-2 w-4 h-4 transition-transform group-hover/btn:translate-x-1 group-hover/btn:-translate-y-1" />
          </Button>
        </CardContent>
      </Card>
    </div>
  );
};

const CaseStudiesSection = () => {
  const [selected, setSelected] = useState<CaseStudy | null>(null);

  return (
    <section id="case-studies" className="py-24 bg-secondary/30" aria-label="SEO case studies">
      <div className="container mx-auto px-6">
        <div className="text-center mb-16">
          <p className="text-primary font-semibold mb-2 uppercase tracking-wider text-sm">Case Studies</p>
          <h2 className="text-4xl md:text-5xl font-bold mb-4">Real Results, Real Numbers</h2>
          <p className="text-muted-foreground text-lg max-w-2xl mx-auto">
            Detailed before/after breakdowns from recent SEO campaigns.
          </p>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-6xl mx-auto">
          {caseStudies.map((cs, i) => (
            <CaseStudyCard key={cs.title} cs={cs} delay={i * 120} onOpen={() => setSelected(cs)} />
          ))}
        </div>
      </div>

      <Dialog open={!!selected} onOpenChange={(open) => !open && setSelected(null)}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          {selected && (
            <>
              <DialogHeader>
                <p className="text-sm text-primary font-semibold uppercase tracking-wider">{selected.niche}</p>
                <DialogTitle className="text-2xl md:text-3xl">{selected.title}</DialogTitle>
                <DialogDescription className="text-base pt-2">{selected.summary}</DialogDescription>
              </DialogHeader>

              <div className="grid sm:grid-cols-3 gap-3 my-6">
                {selected.metrics.map((m) => (
                  <div key={m.label} className="p-4 rounded-lg bg-secondary border border-border">
                    <p className="text-xs text-muted-foreground uppercase tracking-wide mb-1">{m.label}</p>
                    <div className="flex items-baseline gap-2">
                      <span className="text-sm line-through text-muted-foreground">{m.before}</span>
                      <ArrowUpRight className="w-3 h-3 text-primary" />
                      <span className="text-base font-bold">{m.after}</span>
                    </div>
                    <p className="text-primary font-bold text-lg mt-1">{m.growth}</p>
                  </div>
                ))}
              </div>

              <div className="space-y-6">
                <div>
                  <div className="flex items-center gap-2 mb-3">
                    <Target className="w-5 h-5 text-primary" />
                    <h4 className="font-bold text-lg">The Challenge</h4>
                  </div>
                  <p className="text-foreground/80 leading-relaxed">{selected.challenge}</p>
                </div>

                <div>
                  <div className="flex items-center gap-2 mb-3">
                    <BarChart3 className="w-5 h-5 text-primary" />
                    <h4 className="font-bold text-lg">The Solution</h4>
                  </div>
                  <ul className="space-y-2">
                    {selected.solution.map((s, idx) => (
                      <li key={idx} className="flex items-start gap-2 text-foreground/80">
                        <CheckCircle2 className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
                        <span>{s}</span>
                      </li>
                    ))}
                  </ul>
                </div>

                <div>
                  <div className="flex items-center gap-2 mb-3">
                    <TrendingUp className="w-5 h-5 text-primary" />
                    <h4 className="font-bold text-lg">The Results</h4>
                  </div>
                  <ul className="space-y-2">
                    {selected.results.map((r, idx) => (
                      <li key={idx} className="flex items-start gap-2 text-foreground/80">
                        <CheckCircle2 className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
                        <span>{r}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </section>
  );
};

export default CaseStudiesSection;
